import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { useGetRecoveryConfigQuery } from 'api/recoveryAi'
import type { RecoveryConfig } from 'api/recoveryAi.types'
import ModalContainer from 'components/ModalContainer'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import BrandVoiceBody from 'scenes/RecoveryAI/components/BrandVoiceBody'
import {
  collapseBrandVoiceText,
  getDefaultBrandVoiceText,
} from 'scenes/RecoveryAI/components/brandVoiceText'
import BrandVoiceUploadView from 'scenes/RecoveryAI/components/BrandVoiceUploadView'
import SettingsPaneChrome from 'scenes/RecoveryAI/components/SettingsPaneChrome'
import { successNotificationOverrides } from 'scenes/RecoveryAI/components/successNotificationOverrides'
import useBrandVoiceGenerate from 'scenes/RecoveryAI/components/useBrandVoiceGenerate'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import { useSaveAndTest } from './useSaveAndTest'
import { useUnsavedChangesPrompt } from './useUnsavedChangesPrompt'

const t = buildTranslate('recovery_ai.brand_voice')

// Horizontal gap between the two footer buttons (extracted per GAP-1 so the
// layout magic number isn't an inline literal in css({})).
const SAVE_FOOTER_GAP = '16px'

type Props = {
  // Still passed by Settings/index.tsx; unused since AI-1170 removed
  // Save-and-test. Drop with the parent in a follow-up.
  isActivated: boolean
}

// AI-1170: Save-and-test is removed everywhere — testing happens on the
// separate Agent Tuning page instead.
const BrandVoicePane = (_props: Props) => {
  const [css] = useStyletron()
  // AI-64: admin-only writes. A view-only operator (RBAC can-view, not
  // view-and-edit) can reach this route, so the textarea, the "Generate from
  // documents" trigger and both persist buttons all go disabled. Mirrors the
  // CASL gate EscalationRulesPane / DailyBudgetPane already apply.
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')
  const {
    data: config,
    isLoading,
    isError: isFetchError,
  } = useGetRecoveryConfigQuery()

  // W7 (FR-6): one editable buffer prefilled with the Thanx default. seedWasBlank
  // drives the save-collapse so an unedited default persists '' (keeping nexus
  // deriving brand_voice_mode = 'default'). Derived LIVE from the saved config —
  // NOT a hydrate-time snapshot — so it self-corrects after each save on this
  // persistent surface. A snapshot goes stale: clear + save, then retype the
  // default, and the stale `false` would wrongly PUT the whole paragraph.
  const [text, setText] = useState('')
  const seedWasBlank = (config?.brand_voice_text ?? '').trim() === ''

  // "Generate from documents" sub-flow. The staged-file + derive machine is the
  // shared hook (also used by the Agent-Tuning modal) so the two can't drift; the
  // pane owns only its own ModalContainer visibility.
  const [showUpload, setShowUpload] = useState(false)
  const {
    files,
    clientError,
    deriveError,
    isDeriving,
    errorCopyKey,
    addFiles,
    removeFile,
    clearClientError,
    clearDeriveError,
    generate,
    cancel,
  } = useBrandVoiceGenerate()

  // Derive the payload from the buffer. brand_voice_mode is GONE — nexus derives
  // it read-only and drops any value sent, so the single-field shape is all FR-6
  // needs. Collapse an unedited default to '' (only when the seed was blank).
  const buildPayload = useCallback(
    (): Partial<RecoveryConfig> => ({
      brand_voice_text: collapseBrandVoiceText(text, seedWasBlank),
    }),
    [text, seedWasBlank]
  )

  // AI-121 (R4): the form is dirty when the payload differs from the saved config.
  // Pristine forms are non-dirty, so Save can't fire a no-op PUT (+ false banner).
  const isDirty = useMemo(() => {
    if (!config) return false
    return (
      collapseBrandVoiceText(text, seedWasBlank) !==
      (config.brand_voice_text ?? '')
    )
  }, [config, text, seedWasBlank])

  const {
    handleSave,
    clearSuccess,
    saveSuccess,
    isSaving,
    isSaveError,
    canSave,
    guardBypassRef,
  } = useSaveAndTest(buildPayload, isDirty)

  const unsavedChangesPrompt = useUnsavedChangesPrompt(isDirty, guardBypassRef)

  // Hydrate the buffer ONCE on first config load (prefill the default when the
  // persisted voice is blank). RTK Query hands back a new `config` reference on
  // every background refetch, so keying the effect off `config` without the ref
  // would clobber un-saved keystrokes mid-edit. seedWasBlank is derived live above
  // (not seeded here), so it can't go stale after a save.
  const hasHydrated = useRef(false)
  useEffect(() => {
    if (!config || hasHydrated.current) return
    hasHydrated.current = true
    const persisted = config.brand_voice_text ?? ''
    setText(persisted.trim() === '' ? getDefaultBrandVoiceText() : persisted)
  }, [config])

  const handleTextChange = useCallback(
    (next: string) => {
      setText(next)
      clearSuccess()
      // Editing supersedes a prior failed-generate banner, so it can't linger
      // beside a later "saved" banner on this persistent surface.
      clearDeriveError()
    },
    [clearSuccess, clearDeriveError]
  )

  const openUpload = useCallback(() => {
    clearDeriveError()
    setShowUpload(true)
  }, [clearDeriveError])

  const closeUpload = useCallback(() => {
    setShowUpload(false)
    clearClientError()
  }, [clearClientError])

  const handleGenerate = useCallback(async () => {
    clearSuccess()
    // Close the sub-view; the pane's textarea shows the skeleton while deriving.
    setShowUpload(false)
    // On success the hook clears the staged file and hands back the derived text.
    await generate(setText)
  }, [clearSuccess, generate])

  // Clear a prior generate-error banner as a save begins, so a "saved" banner
  // never renders beside it (covers a Save with no intervening edit).
  const handleSaveClick = useCallback(() => {
    clearDeriveError()
    handleSave()
  }, [handleSave, clearDeriveError])

  if (isLoading) {
    return (
      <Spinner isLoading size="4x" className="text-center margin-top-huge" />
    )
  }

  // A failed config fetch must surface an error — not silently render the
  // default-voice form as if it loaded. Mirrors EscalationRulesPane.
  if (isFetchError || !config) {
    return (
      <div className="p-l">
        <Notification kind="negative" size="full">
          {t('fetch_error')}
        </Notification>
      </div>
    )
  }

  return (
    <SettingsPaneChrome title={t('title')} subtitle={t('description')}>
      {/* R3: guard whole-route navigation (in-app + refresh/close) while dirty. */}
      {unsavedChangesPrompt}

      {isSaveError && (
        <div className="mb-m">
          <Notification kind="negative" size="full">
            {t('save_error')}
          </Notification>
        </div>
      )}

      {deriveError && (
        <div className="mb-m">
          <Notification kind="negative" size="full">
            {t(errorCopyKey)}
          </Notification>
        </div>
      )}

      {saveSuccess && !isSaveError && (
        <div className="mb-m">
          <Notification
            kind="positive"
            size="full"
            overrides={successNotificationOverrides}
          >
            {t('save_success')}
          </Notification>
        </div>
      )}

      {/* Collapsed to the chrome-free BrandVoiceBody (AI-62/W7): one textarea +
          the three-way row. The page wrapper keeps the staged Save model + the
          dirty/unsaved-changes guard below. The Generate trigger opens the upload
          sub-view in this pane's own ModalContainer. */}
      <BrandVoiceBody
        value={text}
        onChange={handleTextChange}
        onGenerate={openUpload}
        isDeriving={isDeriving}
        derivingFilenames={files.map(f => f.name)}
        onCancelDerive={cancel}
        disabled={!canManage}
      />

      {/* AI-64: the Save button also disables outright for a view-only
          operator, on top of the dirty/in-flight/deriving gates.
          AI-1170: Save-and-test is removed everywhere — testing happens on
          the separate Agent Tuning page instead. */}
      <div className={`d-flex mt-xl ${css({ gap: SAVE_FOOTER_GAP })}`}>
        <Button
          kind="primary"
          onClick={handleSaveClick}
          disabled={!canManage || !canSave || isDeriving}
          isLoading={isSaving}
        >
          {t('save')}
        </Button>
      </div>

      {showUpload && (
        <ModalContainer
          isOpen
          onBack={closeUpload}
          onClose={closeUpload}
          ariaLabel={t('upload_view_aria_label')}
          hideCancel
          showBottomProceed={false}
          buttonGroupChildren={
            <Button
              kind="primary"
              onClick={handleGenerate}
              disabled={files.length === 0 || isDeriving}
              isLoading={isDeriving}
            >
              {t('generate')}
            </Button>
          }
        >
          <BrandVoiceUploadView
            files={files}
            onAddFiles={addFiles}
            onRemoveFile={removeFile}
            clientError={clientError}
          />
        </ModalContainer>
      )}
    </SettingsPaneChrome>
  )
}

export default BrandVoicePane
